// My Jerky Hut — PDP. Gallery, variant picker, quantity and add-to-cart.
// Adds via /cart/add.js and dispatches Horizon's CartLinesUpdateEvent so the
// cart drawer refreshes and auto-opens (never a redirect). Mirrors the
// approach in jh-build-a-box.js.
import { CartLinesUpdateEvent } from '@shopify/events';

async function refreshCart() {
  const cartItemsComponent = document.querySelector('cart-items-component');
  if (cartItemsComponent) {
    await customElements.whenDefined('cart-items-component');
    return cartItemsComponent.fetchCartData();
  }
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

document.querySelectorAll('[data-jh-pdp]').forEach((root) => {
  const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
  const addLabel = root.dataset.addLabel || 'Add to cart';
  const soldOutLabel = root.dataset.soldOutLabel || 'Sold out';

  const idInput = root.querySelector('[data-jh-variant-id]');
  const qtyInput = root.querySelector('[data-jh-qty]');
  const addBtn = root.querySelector('[data-jh-atc]');
  const addPrice = root.querySelector('[data-jh-atc-price]');
  const liveRegion = root.querySelector('[data-jh-live]');
  const stickyPrice = root.querySelector('[data-jh-sticky-price]');
  const stickyVariant = root.querySelector('[data-jh-sticky-variant]');
  let busy = false;

  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  function currentVariant() {
    return root.querySelector('[data-jh-variant]:checked');
  }

  function qty() {
    return Math.max(1, parseInt(qtyInput?.value || '1', 10) || 1);
  }

  function render() {
    const variant = currentVariant();
    if (!variant) return;
    const available = variant.dataset.available === 'true';
    const price = parseInt(variant.dataset.price, 10) || 0;

    if (idInput) idInput.value = variant.value;
    if (addPrice) addPrice.textContent = formatMoney(price * qty());
    if (stickyPrice) stickyPrice.textContent = formatMoney(price * qty());
    if (stickyVariant) stickyVariant.textContent = variant.dataset.title || '';
    if (addBtn && !busy) {
      addBtn.disabled = !available;
      const label = addBtn.querySelector('[data-jh-atc-label]');
      if (label) label.textContent = available ? addLabel : soldOutLabel;
    }
  }

  // ── Gallery ───────────────────────────────────────────────────────────
  const thumbs = Array.from(root.querySelectorAll('[data-jh-thumb]'));
  const slides = Array.from(root.querySelectorAll('[data-jh-slide]'));
  function showSlide(index) {
    slides.forEach((slide, i) => {
      slide.hidden = i !== index;
    });
    thumbs.forEach((thumb, i) => {
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }
  thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => showSlide(i)));

  // ── Quantity ──────────────────────────────────────────────────────────
  root.querySelector('[data-jh-qty-down]')?.addEventListener('click', () => {
    if (qtyInput) qtyInput.value = String(Math.max(1, qty() - 1));
    render();
  });
  root.querySelector('[data-jh-qty-up]')?.addEventListener('click', () => {
    if (qtyInput) qtyInput.value = String(Math.min(99, qty() + 1));
    render();
  });
  qtyInput?.addEventListener('change', render);

  // ── Variant + selling plan ────────────────────────────────────────────
  root.querySelectorAll('[data-jh-variant]').forEach((input) => input.addEventListener('change', render));
  root.querySelectorAll('[data-jh-selling-plan]').forEach((input) => input.addEventListener('change', render));

  // ── Sticky bar reveals once the buy box scrolls out of view ───────────
  const sticky = root.querySelector('[data-jh-sticky]');
  const buybox = root.querySelector('[data-jh-buybox]');
  if (sticky && buybox && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      ([entry]) => sticky.classList.toggle('is-visible', !entry.isIntersecting),
      { rootMargin: '0px 0px -80% 0px' }
    );
    observer.observe(buybox);
  }

  // ── Add to cart ───────────────────────────────────────────────────────
  function addToCart() {
    const variant = currentVariant();
    if (busy || !variant || variant.dataset.available !== 'true') return;
    busy = true;
    if (addBtn) addBtn.disabled = true;
    const label = addBtn?.querySelector('[data-jh-atc-label]');
    const previousLabel = label?.textContent;
    if (label) label.textContent = 'Adding…';

    const sellingPlan = root.querySelector('[data-jh-selling-plan]:checked')?.value;
    const item = { id: Number(variant.value), quantity: qty() };
    if (sellingPlan) item.selling_plan = Number(sellingPlan);

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    root.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(item.id), quantity: item.quantity }],
        promise: deferred.promise,
      })
    );

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item], sections: sectionIds.join(',') }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          deferred.reject(new Error(response.message || 'Add to cart failed'));
          if (liveRegion) liveRegion.textContent = response.message || 'Could not add to cart';
          return;
        }
        const cart = await refreshCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'jh-main-product',
            itemCount: item.quantity,
            sections: response.sections,
            didError: false,
          },
        });
        if (liveRegion) liveRegion.textContent = 'Added to cart';
        // No drawer on this page → fall back to the cart page
        if (!document.querySelector('cart-drawer-component')) {
          window.location.href = Theme.routes.cart_url;
        }
      })
      .catch((error) => {
        console.error('[jh-main-product]', error);
        deferred.reject(error);
        if (liveRegion) liveRegion.textContent = 'Could not add to cart — please try again';
      })
      .finally(() => {
        busy = false;
        if (label && previousLabel) label.textContent = previousLabel;
        render();
      });
  }

  root.querySelectorAll('[data-jh-atc]').forEach((btn) =>
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      addToCart();
    })
  );

  showSlide(0);
  render();
});
